import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { api, setAuthorizationHeader } from './services/api';

import { UserFE } from '../types';

type UserContextType = {
    user: null | UserFE,
    isAdmin: boolean,
    isMayor: boolean,
    setUser: (user: null | UserFE) => void,
}

const defaultUserContext: Omit<UserContextType, "setUser"> = {
    user: null,
    isAdmin: false,
    isMayor: false,
};


const UserContext = createContext<UserContextType>({
    ...defaultUserContext,
    setUser: () => {},
});


export function UserProvider({ children }: { children: ReactNode }) {
    const [userContext, setUserContext] = useState(defaultUserContext);

    const setUser = useMemo(() => {
        return (user: null | UserFE) => {
            if (user) {
                localStorage.setItem("user", JSON.stringify(user));
                setAuthorizationHeader("bearer " + user.token);
                setUserContext({
                    user,
                    isAdmin: user.roles.includes("admin"),
                    isMayor: user.roles.includes("mayor"),
                });
            } else {
                localStorage.removeItem("user");
                setAuthorizationHeader("");
                setUserContext(defaultUserContext);
            }
        };
    }, []);

    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            let token = "";
            try {
                const user = JSON.parse(storedUser)
                if (typeof user.token === "string")
                    token = user.token;
            } catch (e) {
                console.error(e);
            }

            if (token) {
                setAuthorizationHeader("bearer " + token);
                api.getUser({}).then(([user]) => {
                    if (user) {
                        setUser(user);
                    }
                });
            }
        }
    }, []);

    return <UserContext.Provider value={{ ...userContext, setUser }}>
        {children}
    </UserContext.Provider>
}

export function useUser(): UserContextType {
    return useContext(UserContext);
}